import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { apiService } from '../../../shared/services/apiService';
import { bannerService } from '../../banners/services/bannerService';
import { totemModeService } from '../../../shared/services/totemModeService';
import {
    clearTotemNeedsReconnect,
    totemNeedsReconnect,
} from '../../../shared/services/totemSessionRecovery';
import { useDeviceDetection } from '../../../shared/hooks/useDeviceDetection';
import { useForcedDarkLockRegion } from '../../../shared/hooks/useForcedDarkLockRegion';

interface CodigoResponse {
    codigo: string;
    dataCriacao: string;
}

interface TokenResponse {
    token: string;
    codigoDisplay: number;
}

const TelevisaoPage = () => {
    const navigate = useNavigate();
    const { deviceInfo } = useDeviceDetection();
    const [codigo, setCodigo] = useState<string>('');
    const [empresa, setEmpresa] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [verificando, setVerificando] = useState<boolean>(false);
    const [segundosRestantes, setSegundosRestantes] = useState<number>(4);
    const [isReconnectMode, setIsReconnectMode] = useState<boolean>(() => totemNeedsReconnect());
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null); // Timer separado para contador visual
    const gerandoRef = useRef<boolean>(false); // Proteção contra múltiplas chamadas
    const mountedRef = useRef<boolean>(true);
    const autoReconnectStartedRef = useRef<boolean>(false);
    const qrSectionRef = useRef<HTMLDivElement>(null);
    
    // Tamanho do QR Code: maior quando for TV
    const qrCodeSize = deviceInfo.isTV ? 500 : 300;

    // Só a caixa do QR: fundo branco + código legível mesmo com extensão de dark mode
    useForcedDarkLockRegion(!!codigo, qrSectionRef);

    // Função para validar e sanitizar nome da empresa
    const validarESanitizarEmpresa = (empresaInput: string): string | null => {
        // Remover espaços no início e fim
        const trimmed = empresaInput.trim();
        
        // Validar se está vazio
        if (!trimmed || trimmed === '') {
            return null;
        }
        
        // Validar comprimento máximo (50 caracteres)
        if (trimmed.length > 50) {
            return null;
        }
        
        // Validar formato: apenas letras (a-z, A-Z), números (0-9) e underscore (_)
        // Bloquear caracteres especiais que podem causar problemas (SQL injection, etc)
        const regex = /^[a-zA-Z0-9_]+$/;
        if (!regex.test(trimmed)) {
            return null;
        }
        
        // Retornar em minúsculas (padronização)
        return trimmed.toLowerCase();
    };

    // Inicializar componente
    useEffect(() => {
        mountedRef.current = true;
        
        // Carregar empresa do localStorage se existir (igual ao login)
        // Validar e sanitizar ao carregar do localStorage
        const savedEmpresa = localStorage.getItem('auth_empresa');
        if (savedEmpresa) {
            const empresaSanitizada = validarESanitizarEmpresa(savedEmpresa);
            if (empresaSanitizada) {
                setEmpresa(empresaSanitizada);
            } else {
                // Se não for válida, limpar do localStorage
                localStorage.removeItem('auth_empresa');
            }
        }

        setIsReconnectMode(totemNeedsReconnect());
        
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Gerar código real via API com empresa
    const gerarCodigo = async (empresaOverride?: string) => {
        // Proteção: se já está gerando, não fazer nada
        if (gerandoRef.current) {
            console.log('[TelevisaoPage] ⏭️ Já está gerando código, ignorando...');
            return;
        }

        // Validar e sanitizar empresa
        const empresaSanitizada = validarESanitizarEmpresa(empresaOverride ?? empresa);
        if (!empresaSanitizada) {
            setError('Nome da empresa inválido. Use apenas letras, números e underscore (_). Máximo 50 caracteres.');
            return;
        }

        if (empresaOverride && empresaSanitizada !== empresa) {
            setEmpresa(empresaSanitizada);
        }

        gerandoRef.current = true;
        setLoading(true);
        setError(null);
        setVerificando(false);

        try {
            // Limpar polling e timer anteriores
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }

            // Gerar código real via API com empresaNome no body (já sanitizada e em minúsculas)
            // IMPORTANTE: Para produção, incluir empresa na URL: /r/{empresa}/v1/banner/televisao/codigo/gerar
            // Para localhost, usar URL normal: /v1/banner/televisao/codigo/gerar
            // Isso é APENAS para este endpoint específico, não afeta outros endpoints
            
            // Verificar se é produção (não localhost)
            const apiServer1 = process.env.REACT_APP_API_SERVER_1 || '';
            const isProduction = apiServer1.includes('market.net.br') || 
                                apiServer1.includes('market.srv.br');
            
            let fullUrl: string | null = null;
            if (isProduction && empresaSanitizada) {
                // Em produção, construir URL completa removendo empresa padrão da base
                // A base pode vir como https://www.market.net.br/r ou https://www.market.net.br/r/dev
                // Precisamos garantir que seja https://www.market.net.br/r/{empresa}
                let baseUrl = apiServer1;
                // Se a base já tem /r/empresa, remover a empresa
                if (baseUrl.match(/\/r\/[^/]+$/)) {
                    baseUrl = baseUrl.replace(/\/r\/[^/]+$/, '/r');
                }
                // Se não tem /r, adicionar
                if (!baseUrl.endsWith('/r')) {
                    baseUrl = baseUrl.endsWith('/') ? `${baseUrl}r` : `${baseUrl}/r`;
                }
                fullUrl = `${baseUrl}/${empresaSanitizada}/v1/banner/televisao/codigo/gerar`;
            }
            
            console.log(`[TelevisaoPage] Endpoint usado: ${fullUrl || '/v1/banner/televisao/codigo/gerar'}`);
            
            let response: CodigoResponse;
            if (fullUrl) {
                // Produção: fazer fetch direto com URL completa
                const token = apiService.getToken();
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json'
                };
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                const fetchResponse = await fetch(fullUrl, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        empresaNome: empresaSanitizada
                    })
                });
                
                if (!fetchResponse.ok) {
                    throw new Error(`Erro ${fetchResponse.status}: ${fetchResponse.statusText}`);
                }
                
                const responseText = await fetchResponse.text();
                if (!responseText || responseText.trim() === '') {
                    throw new Error('Resposta vazia do servidor');
                }
                
                response = JSON.parse(responseText);
            } else {
                // Localhost: usar apiService normalmente
                response = await apiService.post<CodigoResponse>('/v1/banner/televisao/codigo/gerar', {
                    empresaNome: empresaSanitizada
                });
            }
            
            if (!mountedRef.current) {
                return;
            }

            if (response && response.codigo) {
                setCodigo(response.codigo);
            } else {
                throw new Error('Código não foi gerado');
            }
        } catch (err: any) {
            if (!mountedRef.current) return;
            const errorMessage = err instanceof Error ? err.message : 'Erro ao gerar código';
            setError(errorMessage);
            console.error('[TelevisaoPage] Erro ao gerar código:', err);
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                gerandoRef.current = false;
            }
        }
    };


    // Função para buscar token e navegar para a tela
    const buscarTokenENavegar = async (codigoParaBuscar: string) => {
        if (!mountedRef.current) {
            return false;
        }
        
        try {
            setVerificando(true);
            // Buscar token via POST com empresa (a empresa será obtida do código gerado)
            // O endpoint agora é POST /v1/banner/televisao/codigo/token com { codigo, empresa }
            // Passar empresa sanitizada para buscar token (necessário em produção)
            const empresaSanitizada = validarESanitizarEmpresa(empresa);
            const resultado = await bannerService.buscarTokenPorCodigo(codigoParaBuscar, empresaSanitizada ?? undefined);

            if (!mountedRef.current) {
                return false;
            }

            if (resultado && resultado.token) {
                // Token encontrado! Configurar token
                apiService.setToken(resultado.token);
                clearTotemNeedsReconnect();
                setIsReconnectMode(false);

                // IMPORTANTE: Configurar empresa no apiService para garantir que todas as requisições usem a empresa correta
                // A empresa já foi validada e sanitizada anteriormente
                const empresaSanitizada = validarESanitizarEmpresa(empresa);
                if (empresaSanitizada) {
                    apiService.setCompany(empresaSanitizada);
                }

                // Ativar modo totem
                totemModeService.enableTotemMode();

                // OPERAÇÃO 1: TELEVISÃO (cdTelevisao direto na resposta)
                if (resultado.cdTelevisao !== null && resultado.cdTelevisao !== undefined) {
                    navigate(`/display/televisao/${resultado.cdTelevisao}?mode=totem`);
                    return true;
                }

                // OPERAÇÃO 2: TELA INDIVIDUAL (apenas quando NÃO for televisão)
                if (resultado.codigoDisplay !== null && resultado.codigoDisplay !== undefined && !resultado.cdTelevisao) {
                    navigate(`/display/${resultado.codigoDisplay}?mode=totem`);
                    return true;
                }

                // OPERAÇÃO 3: SEQUÊNCIA (compatibilidade - apenas quando não for televisão nem tela)
                if (resultado.nomeSequencia && !resultado.cdTelevisao && !resultado.codigoDisplay) {
                    navigate(`/display/sequencia/${encodeURIComponent(resultado.nomeSequencia)}?mode=totem`);
                    return true;
                }

                console.error('[TelevisaoPage] Resposta inválida do backend');
            }
        } catch (err) {
            console.error('[TelevisaoPage] Erro ao buscar token:', err);
        } finally {
            if (mountedRef.current) {
                setVerificando(false);
            }
        }
        return false;
    };

    // Reconexão após sessão expirada: gera QR automaticamente com a empresa já salva (sem digitar na TV)
    useEffect(() => {
        if (!isReconnectMode || autoReconnectStartedRef.current) {
            return;
        }

        const savedEmpresa = validarESanitizarEmpresa(
            empresa || localStorage.getItem('auth_empresa') || ''
        );
        if (!savedEmpresa) {
            return;
        }

        autoReconnectStartedRef.current = true;
        if (!empresa) {
            setEmpresa(savedEmpresa);
        }
        void gerarCodigo(savedEmpresa);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só na entrada em modo reconexão
    }, [isReconnectMode, empresa]);

    // Effect para gerenciar polling a cada 4 segundos para verificar se o token foi gerado
    useEffect(() => {
        // Se não tem código, não faz nada
        if (!codigo) {
            // Limpar timers se não tiver código
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            return;
        }

        // Resetar timer visual ao mudar o código
        setSegundosRestantes(4);

        // Timer visual separado que atualiza a cada 1 segundo
        const timerId = setInterval(() => {
            if (!mountedRef.current) {
                clearInterval(timerId);
                return;
            }

            setSegundosRestantes(prev => {
                if (prev <= 1) {
                    return 4; // Reinicia o contador para 4
                }
                return prev - 1;
            });
        }, 1000); // Atualiza a cada 1 segundo

        timerIntervalRef.current = timerId;

        // Polling a cada 4 segundos para verificar se o token foi gerado
        const pollingId = setInterval(async () => {
            if (!mountedRef.current) {
                clearInterval(pollingId);
                return;
            }

            const sucesso = await buscarTokenENavegar(codigo);
            if (sucesso) {
                clearInterval(pollingId);
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }
                pollingIntervalRef.current = null;
                return;
            }

            // Resetar contador visual após cada polling
            setSegundosRestantes(4);
        }, 4000); // Polling a cada 4 segundos

        pollingIntervalRef.current = pollingId;

        // CLEANUP: Matar os intervalos quando o componente desmontar ou o código mudar
        return () => {
            clearInterval(pollingId);
            clearInterval(timerId);
            pollingIntervalRef.current = null;
            timerIntervalRef.current = null;
        };
    }, [codigo]); // Dependência crucial: recria o efeito se o código mudar

    return (
        <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 overflow-y-auto">
            <div className="w-full max-w-4xl">
                <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center relative">
                    {!isReconnectMode && (
                        <button
                            onClick={() => navigate('/login')}
                            className="absolute top-4 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            <ArrowLeft size={20} />
                            <span className="text-sm font-medium">Voltar ao Login</span>
                        </button>
                    )}
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
                        {isReconnectMode ? 'Sessão expirada' : 'Código de Totem'}
                    </h1>
                    {isReconnectMode && (
                        <p className="text-lg text-gray-600 mb-8">
                            Escaneie o QR Code no celular para reconectar a TV
                        </p>
                    )}
                    {!isReconnectMode && <div className="mb-8" />}

                    {/* Formulário de empresa e gerar código */}
                    {!codigo && (!isReconnectMode || !empresa.trim()) && (
                        <div className="py-8">
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                    Empresa *
                                </label>
                                <input
                                    type="text"
                                    value={empresa}
                                    onChange={(e) => {
                                        const valor = e.target.value;
                                        // Validar em tempo real: apenas letras, números e underscore
                                        // Limitar a 50 caracteres
                                        const regex = /^[a-zA-Z0-9_]*$/;
                                        if (regex.test(valor) && valor.length <= 50) {
                                            setEmpresa(valor);
                                            setError(null);
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            gerarCodigo();
                                        }
                                    }}
                                    maxLength={50}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Digite o nome da empresa"
                                    disabled={loading}
                                />
                            </div>

                            <button
                                onClick={() => gerarCodigo()}
                                disabled={loading || !empresa.trim()}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Gerando...' : 'Gerar Código'}
                            </button>
                        </div>
                    )}

                    {loading && (
                        <div className="py-20">
                            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
                            <p className="mt-4 text-gray-600 text-lg">
                                {isReconnectMode ? 'Preparando reconexão...' : 'Gerando código...'}
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="py-8">
                            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-4">
                                <p className="font-semibold">Erro ao gerar código</p>
                                <p className="mt-2">{error}</p>
                            </div>
                            <button
                                onClick={() => gerarCodigo()}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                            >
                                Tentar Novamente
                            </button>
                        </div>
                    )}

                    {/* QR Code e código - aparece depois de gerar */}
                    {!loading && !error && codigo && (
                        <div className="py-8">
                            {/* QR Code - Agora como foco principal */}
                            <div className="mb-8 flex flex-col items-center">
                                <p className="text-xl font-medium text-gray-700 mb-6">
                                    {isReconnectMode
                                        ? 'Escaneie para reconectar'
                                        : 'Escaneie o QR Code para vincular a tela'}
                                </p>
                                <div
                                    ref={qrSectionRef}
                                    className="bg-white p-6 rounded-2xl shadow-xl transition-transform hover:scale-105 duration-300"
                                >
                                    <QRCodeSVG
                                        value={codigo}
                                        size={qrCodeSize}
                                        level="H"
                                        includeMargin={true}
                                        fgColor="#000000"
                                        bgColor="#ffffff"
                                    />
                                </div>
                            </div>

                            {/* Código numérico em texto - Menor e abaixo */}
                            <div className="mb-6">
                                <p className="text-gray-500 text-sm font-semibold mb-2 uppercase tracking-wide">
                                    Código Manual
                                </p>
                                <p className="text-4xl font-mono font-bold text-gray-800 tracking-wider">
                                    {codigo}
                                </p>
                            </div>

                            <div className="mb-4 flex items-center justify-center gap-2 text-blue-600">
                                <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600"></div>
                                <p className="text-sm font-medium">
                                    Aguardando vínculo... (verificando novamente em {segundosRestantes}s)
                                </p>
                            </div>

                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={() => gerarCodigo()}
                                    disabled={loading || !empresa.trim()}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Gerando...' : 'Gerar Novo Código'}
                                </button>
                                {!isReconnectMode && (
                                    <button
                                        onClick={() => {
                                            setCodigo('');
                                            setError(null);
                                        }}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg transition-colors"
                                    >
                                        Trocar Empresa
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TelevisaoPage;

